<template>
  <div @click.shift="editRessource()">
    <div v-if="!hideLabel" class="edit-field-label">{{ label }}</div>
    <v-text-field ref="TEXT_FIELD"
                  v-model=saisie
                  :class="getEditFieldClass"
                  :disabled=disabled
                  :error=displayError
                  :error-messages="displayError ? errorText : ''"
                  :hide-details="dataTable ? dataTable : false"
                  :label=label
                  variant="outlined"
                  :placeholder="placeholder"
                  :readonly="readOnly"
                  :ripple=false
                  :single-line="true"
                  :type="type"
                  :append-inner-icon="passwordIcon"
                  persistent-placeholder
                  @click:append-inner="toggleShowPassword()"
                  @blur="onBlur()"
                  @focus="$event.target.select();onFocus()">
      <template #append>
        <v-icon v-if="cloneEditFieldInfo.ressource.helpHtmlId > 0"
                class="help-icon"
                @click="toggleHelpDialog">
          fas fa-circle-info
        </v-icon>
      </template>
    </v-text-field>

    <help-dialog v-if="showHelpDialog && cloneEditFieldInfo.ressource.helpHtmlId > 0"
                 :content="helpHtml"
                 :open="showHelpDialog"
                 @close="toggleHelpDialog">
    </help-dialog>
  </div>
</template>

<script lang="ts">
  import Validator, { RULES_FRENCH_LABEL } from '@/generated/validators/ValidationRules';
  import {
    EditFieldTheme,
    EditFieldType,
    FieldError,
    FieldInfo,
    type FocusValidator,
    type LiveValidator
  } from '@/framework/base/components/FieldInfo';
  import { Period } from '@/framework/base/helpers/DateFormat';
  import { getCurrency } from '@/framework/base/helpers/Localization';
  import { type EditRessource } from '@/framework/base/helpers/Ressources';
  import { Ressources } from '@/framework/base/helpers/Ressources';
  import HelpDialog from '@/framework/base/components/HelpDialog.vue';
  import { type Mask } from '@/framework/base/components/FieldInfo';
  import { defineComponent, type PropType } from "vue";
  import { storeSession } from '@/generated/stores';
  import UserProfile, { EnumProfile } from '@/framework/base/classes/UserProfile';

  export let cbEditRessource: EditRessource | undefined = undefined;

  export function setEditRessource(editRessource: EditRessource) {
    cbEditRessource = editRessource;
  }

  export default defineComponent({
    components: { HelpDialog },
    props: {
      value: {
        type: [String, Date, Number, null],
        required: true
      },
      contextId: {
        type: Number,
        required: true
      },
      min: {
        type: [Number, String]
      },
      max: {
        type: [Number, String]
      },
      periods: {
        type: Array as PropType<Period[]>
      },
      editFieldInfo: {
        type: Object as PropType<FieldInfo>,
        required: true
      },
      required: {
        type: Boolean
      },
      disabled: {
        type: Boolean
      },
      readOnly: {
        type: Boolean
      },
      parentFocus: {
        type: Boolean
      },
      bold: {
        type: Boolean
      },
      hideLabel: {
        type: Boolean
      },
      dataTable: {
        type: Boolean
      },
      password: {
        type: Boolean
      },
      showMissing: {
        type: Boolean
      },
      overRidePlaceholder: {
        type: String
      }
    },
    created(): void {
      this.lastValue = this.value;
      if (this.mask && this.disabled) {
        this.lastValue = this.maskFormat(this.value, this.mask);
      }
      this.error = false;
      if (this.editFieldInfo.type == EditFieldType.STRING) {
        this.cloneEditFieldInfo.compare = this.compareString;
      } else if (this.editFieldInfo.type == EditFieldType.INT) {
        this.cloneEditFieldInfo.compare = this.compareInt;
      } else if (this.editFieldInfo.type == EditFieldType.DECIMAL) {
        this.cloneEditFieldInfo.compare = this.compareDecimal;
      } else if (this.editFieldInfo.type == EditFieldType.CURRENCY) {
        this.cloneEditFieldInfo.compare = this.compareDecimal;
      }
      this.label = this.cloneEditFieldInfo.ressource.label;
    },
    data() {
      const lastLiveValidator = null as Validator | null;
      const lastFocusValidator = null as Validator | null;
      const showErrorTimeout = undefined as ReturnType<typeof setTimeout> | undefined;
      const cloneEditFieldInfo = FieldInfo.fromJson(this.editFieldInfo);

      return {
        cbEditRessource,
        lastValue: '',
        lastContextId: 0,
        error: false,
        getFocus: false,
        lostFocus: false,
        label: '',
        errorText: '',
        editing: false,
        showHelpDialog: false,
        loadedHelpHtml: '',
        displayError: false,
        isMaskRespected: true,
        isMaskCompleted: true,
        showErrorTimeout,
        showPasswordValue: false,
        lastFocusValidator,
        lastLiveValidator,
        lastMask: '',
        cloneEditFieldInfo
      };
    },
    computed: {
      EditFieldTheme() {
        return EditFieldTheme;
      },
      getEditFieldClass(): string {
        let res = 'edit-field';
        if (this.bold) {
          res = 'edit-field-bold';
        }
        if (this.dataTable) {
          res += ' data-table-custom';
        }
        if (this.editFieldInfo.type == EditFieldType.CURRENCY && this.dataTable) {
          res += ' currency-input';
        }
        return res;
      },
      type(): string {
        let res = 'text';
        if (this.password && !this.showPassword) {
          res = 'password';
        }
        return res;
      },
      showPassword: {
        get(): boolean {
          return this.showPasswordValue;
        },
        set(value: boolean) {
          this.showPasswordValue = value;
        }
      },
      passwordIcon(): string {
        let res = '';
        if (this.password) {
          if (this.showPassword) {
            res = 'fas fa-eye-slash';
          } else {
            res = 'fas fa-eye';
          }
        }
        return res;
      },
      placeholder(): string {
        if (this.overRidePlaceholder) {
          return this.overRidePlaceholder;
        } else if (this.cloneEditFieldInfo.ressource.placeholder) {
          if (this.mask) {
            return 'Ex : ' + this.cloneEditFieldInfo.ressource.placeholder;
          } else {
            return this.cloneEditFieldInfo.ressource.placeholder;
          }
        } else {
          return '';
        }
      },
      mask(): string {
        let res = this.lastMask;
        if (this.cloneEditFieldInfo.mask != null) {
          const mask = this.cloneEditFieldInfo.mask as Mask;
          res = mask(this.lastValue);
          this.setLastMask(res);
        } else {
          this.setLastMask('');
        }
        return res;
      },
      liveValidator(): Validator | null {
        let res = this.lastLiveValidator;
        if (this.value != this.lastValue && this.cloneEditFieldInfo.liveValidator != null) {
          const liveValidator = this.cloneEditFieldInfo.liveValidator as LiveValidator;
          res = liveValidator(this.lastValue);
          this.setLastLiveValidator(res);
        } else {
          this.setLastMask('');
        }
        return res;
      },
      focusValidator(): Validator | null {
        let res = this.lastFocusValidator;
        if (this.cloneEditFieldInfo.focusValidator != null) {
          const focusValidator = this.cloneEditFieldInfo.focusValidator as FocusValidator;
          res = focusValidator(this.lastValue);
          this.setLastFocusValidator(res);
        } else {
          this.setLastMask('');
        }
        return res;
      },
      saisie: {
        get(): string {
          let value = this.value;
          if (this.value) {
            value = this.value.toString();
            value = value.trim();
          }
          if (this.contextId != this.lastContextId) {
            this.checkError(value, false);
            this.setLastContextId(this.contextId);
            this.$emit('context', this.value);
          }
          if (value != this.lastValue) {
            // Application du mask de saisie
            if (this.mask) {
              value = this.maskFormat(value, this.mask);
            }
            // Contrôle de la valeur
            this.checkError(value, false);
            this.setLastValue(value);
          }

          if (this.showMissing) {
            this.checkError(value, true);
          }

          // Formatage des données sur perte de focus
          if (!this.error && !this.editing) {
            if (this.cloneEditFieldInfo.type === EditFieldType.DECIMAL) {
              value = this.formatDecimal(value, this.cloneEditFieldInfo.precision ? this.cloneEditFieldInfo.precision : 2);
            } else if ((!this.getFocus || this.lostFocus) && this.cloneEditFieldInfo.type === EditFieldType.CURRENCY) {
              value = this.formatDecimal(value, 2);
              value = this.formatCurrency(value);
            } else if (this.cloneEditFieldInfo.type === EditFieldType.PERCENTAGE) {
              value = this.formatPercentage(value, this.cloneEditFieldInfo.precision ? this.cloneEditFieldInfo.precision : 2);
            }
          }
          this.setGetFocus(false);
          this.setLostFocus(false);

          return value;
        },
        set(value: string) {
          this.lastValue = value;
          this.editing = true;

          // Mise en forme des données saisie
          value = this.cloneEditFieldInfo.format ? this.cloneEditFieldInfo.format(value) : value;
          value.toString();
          value = this.cloneEditFieldInfo.type === EditFieldType.DECIMAL || EditFieldType.CURRENCY ? value.replace(',', '.') : value;
          value = value.trim();

          if (this.mask) {
            value = this.maskFormat(value, this.mask);
          }

          // Contrôle de la saisie
          this.checkError(value, false);

          // Pseudo binding, génère l'appel du getter
          if (this.value != value) {
            this.$emit('updated', value);
          }
        }
      },
      minLimit(): number | undefined {
        return this.cloneEditFieldInfo.compare(this.min, true);
      },
      maxLimit(): number | undefined {
        return this.cloneEditFieldInfo.compare(this.max, true);
      },
      helpHtml(): string {
        if (this.showHelpDialog && this.loadedHelpHtml == '' && this.cloneEditFieldInfo.ressource.helpHtmlId > 0) {
          this.getHelpResources(this.cloneEditFieldInfo.ressource.helpHtmlId);
        }
        return this.loadedHelpHtml;
      }
    },
    methods: {
      setLastLiveValidator(value: Validator | null) {
        this.lastLiveValidator = value;
      },
      setLastFocusValidator(value: Validator | null) {
        this.lastFocusValidator = value;
      },
      setGetFocus(value: boolean) {
        this.getFocus = value;
      },
      setLostFocus(value: boolean) {
        this.lostFocus = value;
      },
      setLastValue(value: string) {
        this.lastValue = value;
      },
      setLastMask(value: string) {
        this.lastMask = value;
      },
      setLastContextId(value: number) {
        this.lastContextId = value;
      },
      getHelpResources(value: number) {
        Ressources.getHelp(value).then((res) => {
          this.loadedHelpHtml = res.html;
        });
      },
      toggleShowPassword() {
        this.showPassword = !this.showPasswordValue;
      },
      compareString(value: string | number | undefined, limit: boolean): number | undefined {
        if (value != undefined && value != '' && limit) {
          return parseInt(value.toString());
        } else if (value != undefined && value != '') {
          return value.toString().length;
        } else {
          return undefined;
        }
      },
      compareInt(value: string | number | undefined, limit: boolean): number | undefined {
        if (value != undefined && value != '') {
          return parseInt(value.toString());
        } else {
          return undefined;
        }
      },
      compareDecimal(value: string | number | undefined, limit: boolean): number | undefined {
        if (value != undefined && value != '') {
          return parseFloat(value.toString());
        } else {
          return undefined;
        }
      },
      maskFormat(value: string, mask: string): string {
        this.isMaskRespected = true;
        this.isMaskCompleted = true;
        if (value && mask) {
          let countValue = 0;
          let countMask = 0;
          // defined part of mask used
          while (countValue < value.length && countMask < mask.length) {
            const valueCar = value.substring(countValue, countValue + 1);
            const maskCar = mask.substring(countMask, countMask + 1);
            const carWaited = maskCar == 'A' || maskCar == 'N' || maskCar == 'X';
            if (carWaited) {
              if ((maskCar == 'A' && this.isLetter(valueCar)) ||
                (maskCar == 'N' && this.isDigit(valueCar)) ||
                (maskCar == 'X' && (this.isDigit(valueCar) || this.isLetter(valueCar)))) {
                countValue++;
                countMask++;
              } else {
                this.isMaskRespected = false;
                break;
              }
            } else if (valueCar == maskCar) {
              countValue++;
              countMask++;
            } else {
              countMask++;
            }
          }
          let res = value;
          if (this.isMaskRespected) {
            // Transform with limited mask
            res = this.applyMask(value, mask.substring(0, countMask));
            // Add extra mask caracters
            res += value.substring(countValue, value.length);
          }

          if (res.length > mask.length) {
            this.isMaskRespected = false;
          }

          this.isMaskCompleted = (res.length === mask.length) && this.isMaskRespected;

          return res;
        } else {
          return value;
        }
      },
      unMaskFormat(value: string, mask: string): string {
        if (value && mask) {
          // Format value
          value = this.maskFormat(value, mask);
          let res = '';
          let countValue = 0;
          let countMask = 0;
          // Clear mask caracters
          while (countValue < value.length && countMask < mask.length) {
            const valueCar = value.substring(countValue, countValue + 1);
            const maskCar = mask.substring(countMask, countMask + 1);
            const carWaited = maskCar == 'A' || maskCar == 'N' || maskCar == 'X';
            if (carWaited) {
              res += valueCar;
            }
            countValue++;
            countMask++;
          }
          // Add extra mask caracters
          if (value.length > mask.length) {
            res += value.substring(mask.length, value.length);
          }
          return res;
        } else {
          return value;
        }
      },
      applyMask(value: string, mask: string): string {
        let res = ''
        let pos = 0;
        for (let cpt = 0; cpt < mask.length; cpt++) {
          const valueCar = value.substring(pos, pos + 1);
          const maskCar = mask.substring(cpt, cpt + 1);
          if (maskCar == valueCar || maskCar == 'A' || maskCar == 'N' || maskCar == 'X') {
            res += value.substring(pos, pos + 1);
            pos++;
          } else {
            res += maskCar;
          }
        }
        return res;
      },
      missing(): boolean {
        return this.required && (!this.value || this.value == '');
      },
      touch(): boolean {
        this.lostFocus = true;
        return this.checkError(this.value, true);
      },
      onBlur() {
        this.lostFocus = true;
        if (!this.parentFocus) {
          this.checkError(this.lastValue, true);
          this.editing = false;
        }
      },
      onFocus() {
        this.getFocus = true;
      },
      setFocus(): void {
        if (this.$refs.TEXT_FIELD) {
          // @ts-expect-error : generated method
          this.$refs.TEXT_FIELD.focus();
        }
      },
      isLetter(char: string): boolean {
        return /[a-zA-Z]/.test(char);
      },
      isDigit(char: string): boolean {
        return /[0-9]/.test(char);
      },
      checkError(value: string, displayError: boolean): boolean {
        let error = false;
        let errorLabel = '';
        clearTimeout(this.showErrorTimeout);

        let minErrorText = '';
        if (this.min) {
          if (this.cloneEditFieldInfo.type === EditFieldType.STRING) {
            minErrorText = RULES_FRENCH_LABEL.text.minStringLength.error.replace('$MIN', this.min.toString());
          } else {
            minErrorText = RULES_FRENCH_LABEL.text.minValue.error.replace('$MIN', this.min.toString());
          }
        }

        let maxErrorText = '';
        if (this.max) {
          if (this.cloneEditFieldInfo.type === EditFieldType.STRING) {
            maxErrorText = RULES_FRENCH_LABEL.text.maxStringLength.error.replace('$MAX', this.max.toString());
          } else {
            maxErrorText = RULES_FRENCH_LABEL.text.maxValue.error.replace('$MAX', this.max.toString());
          }
        }

        // vérifie que les champs requis sont renseignés
        if (!value || value == '') {
          if (this.required && displayError) {
            error = true;
            errorLabel = this.label + ' (' + RULES_FRENCH_LABEL.text.required.error + ')';
          }
        } else {
          // Vérification du respect du mask
          if (!error) {
            if (this.mask) {
              const maskError = 'Format invalide : Exemple ' + this.cloneEditFieldInfo.ressource.placeholder;
              if (!this.isMaskRespected) {
                error = true;
                errorLabel = maskError;
                displayError = true;
              } else if (!this.isMaskCompleted) {
                error = true;
                errorLabel = maskError;
              } else if (this.value == this.cloneEditFieldInfo.ressource.placeholder) {
                error = true;
                errorLabel = this.value + ' est un exemple';
              }
            }
          }

          const unmaskedValue = this.unMaskFormat(value, this.mask);

          // Vérification des validateurs en live
          if (!error) {
            const liveValidator = this.liveValidator;
            if (liveValidator) {
              let testedValue = unmaskedValue;
              if (liveValidator.rule.useMask) {
                testedValue = value;
              }
              if (!liveValidator.test(testedValue)) {
                error = true;
                displayError = true;
                errorLabel = liveValidator.label;
              }
            }
          }
          // Vérification des validateurs sur perte focus
          if (!error && this.lostFocus) {
            const focusValidator = this.focusValidator;
            if (focusValidator) {
              let testedValue = unmaskedValue;
              if (focusValidator.rule.useMask) {
                testedValue = value;
              }
              if (!focusValidator.test(testedValue)) {
                error = true;
                errorLabel = focusValidator.label;
              }
            }
          }

          // Vérification de la valeur est supérieure au min
          if (!error) {
            if (this.editing && this.cloneEditFieldInfo.compare != undefined) {
              if (this.minLimit != undefined) {
                const compareMin = this.cloneEditFieldInfo.compare(value, false);
                if (compareMin != undefined) {
                  if (this.minLimit > compareMin) {
                    error = true;
                    errorLabel = minErrorText;
                  }
                }
              }
            }
          }

          // Vérification de la valeur est inférieure au max
          if (!error) {
            if (this.cloneEditFieldInfo.compare != undefined) {
              if (this.maxLimit != undefined) {
                const compareMax = this.cloneEditFieldInfo.compare(value, false);
                if (compareMax != undefined) {
                  if (this.maxLimit < compareMax) {
                    error = true;
                    errorLabel = maxErrorText;
                    displayError = true;
                  }
                }
              }
            }
          }

          // Vérification de periods
          if (!error) {
            if (this.cloneEditFieldInfo.valid != undefined && this.periods) {
              const res = this.cloneEditFieldInfo.valid(value, this.periods);
              if (!res) {
                error = true;
                errorLabel = RULES_FRENCH_LABEL.text.periodError.error;
                displayError = true;
              }
            }
          }
        }

        // Traitement des erreurs
        if (error && !displayError) {
          // Si erreur non affichée, timout
          this.displayError = false;
          this.showErrorTimeout = setTimeout(() => this.checkError(value, true), 1500);
        } else if (error && displayError) {
          // Si erreur affichée
          this.errorText = errorLabel;
          this.displayError = true;
        } else {
          // Si pas d'erreur
          this.errorText = this.label;
          this.displayError = false;

        }
        // Envoi du status d'erreur
        if (error != this.error) {
          this.error = error;
          this.$emit('error', new FieldError(0, '', this.error));
        }

        return error;
      },
      formatDecimal(value: string, precision: number): string {
        if (value) {
          return parseFloat(value.toString().replace(',', '.')).toFixed(precision).replace('.', ',');
        }
        return '';
      },
      formatCurrency(value: string): string {
        if (value) {
          return getCurrency(parseFloat(value.toString().replace(',', '.')));
        } else {
          return '';
        }
      },
      formatPercentage(value: string): string {
        if (value) {
          return parseFloat(value.toString().replace(',', '.')).toFixed(2).replace('.', ',') + ' %';
        } else {
          return '';
        }
      },
      editRessource() {
        const user = storeSession.connectedUser;
        if (user) {
          const isAdmin = UserProfile.hasProfile(user, EnumProfile.admin);
          if (isAdmin && cbEditRessource) {
            this.loadedHelpHtml = 'editing';
            this.showHelpDialog = false;
            cbEditRessource(this.cloneEditFieldInfo.ressource, this.updateRessource);
          }
        }
      },
      updateRessource() {
        this.showHelpDialog = true;
        this.loadedHelpHtml = '';
      },
      toggleHelpDialog() {
        this.showHelpDialog = !this.showHelpDialog;
      }
    }
  })

</script>